import { Button, Card, Illustration, Logo } from '@tomo/ui';
import { ArrowRight, Cloud, HardDrive, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCloud } from '../../data/cloud/session.ts';
import { useIdentity } from '../../data/identity.ts';
import { useLocalProjects } from '../../data/registry.ts';
import { greeting } from '../../lib/time.ts';
import { AuthDialog, type AuthMode } from '../account/AuthDialog.tsx';
import { ProjectTile } from '../shell/ProjectTile.tsx';
import { UserButton } from '../shell/Sidebar.tsx';
import { NewProjectFields, useNewProjectForm } from './NewProjectForm.tsx';
import { importProjectFile, loadCloudProjects, openCloudProject, openSampleProject, type WorkspaceProjects } from './project-actions.ts';

/**
 * `/new`: where Tomo lands when this device has no project (a first visit, or the last one was
 * deleted). Start a project, open the sample, import a file, or open one from a cloud workspace.
 */
export function NewProjectPage() {
  const navigate = useNavigate();
  const me = useIdentity();
  const cloud = useCloud();
  const local = useLocalProjects();
  const form = useNewProjectForm((id) => navigate(`/p/${id}`, { replace: true }));
  const [busy, setBusy] = useState<'sample' | 'import' | null>(null);
  const [auth, setAuth] = useState<AuthMode | null>(null);
  const [remote, setRemote] = useState<WorkspaceProjects[]>([]);

  useEffect(() => {
    if (cloud.status !== 'signed-in') {
      setRemote([]);
      return;
    }
    let alive = true;
    void loadCloudProjects(cloud.workspaces).then((list) => alive && setRemote(list));
    return () => {
      alive = false;
    };
  }, [cloud]);

  const run = async (what: 'sample' | 'import', action: () => Promise<void>) => {
    setBusy(what);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  const onDevice = new Set(local.map((p) => p.id));
  const cloudProjects = remote.flatMap(({ workspace, projects }) => projects.filter((p) => !onDevice.has(p.id)).map((project) => ({ project, workspace })));

  return (
    <div className="create">
      <header className="create__bar">
        <Logo size={24} />
        <UserButton />
      </header>

      <main className="create__main">
        <section className="create__hero">
          <p className="g-overline">
            {greeting()}
            {me.name && me.name !== 'You' ? `, ${me.name}` : ''}
          </p>
          <h1 className="create__title">Create a project</h1>
          <p className="create__subtitle">One tome for each game: every unit, mechanic, idea and asset in one place.</p>
        </section>

        <Card padding="lg" className="create__card">
          <NewProjectFields form={form} />
          <div className="create__submit">
            {local.length > 0 && (
              <Button variant="ghost" size="lg" onClick={() => navigate('/')}>
                Cancel
              </Button>
            )}
            <Button variant="primary" size="lg" iconRight={<ArrowRight />} loading={form.busy} disabled={!form.name.trim()} shortcut="mod+enter" onClick={form.submit}>
              Create project
            </Button>
          </div>
        </Card>

        <section className="create__others" aria-label="Other ways to start">
          <button type="button" className="create__option create__option--sample" disabled={busy !== null} onClick={() => void run('sample', () => openSampleProject(navigate))}>
            <Illustration name="chest" size={64} />
            <span className="create__option-text">
              <span className="create__option-title">{busy === 'sample' ? 'Opening Emberfall…' : 'Explore Emberfall'}</span>
              <span>A sample fantasy RTS: units with DPS formulas, design docs, votes, a board and art.</span>
            </span>
            <ArrowRight className="create__option-arrow" />
          </button>
          <button type="button" className="create__option" disabled={busy !== null} onClick={() => void run('import', () => importProjectFile(navigate))}>
            <span className="create__option-icon">
              <Upload />
            </span>
            <span className="create__option-text">
              <span className="create__option-title">Import a .tomo file</span>
              <span>Open a project exported from Tomo, on this device or another.</span>
            </span>
            <ArrowRight className="create__option-arrow" />
          </button>
        </section>

        {cloudProjects.length > 0 && (
          <section className="create__cloud">
            <h2 className="g-overline create__cloud-title">
              <Cloud /> In your cloud workspaces
            </h2>
            <div className="create__cloud-list">
              {cloudProjects.map(({ project, workspace }) => (
                <button key={project.id} type="button" className="create__cloud-item" onClick={() => void openCloudProject(navigate, project, workspace)}>
                  <ProjectTile icon={project.icon ?? 'i:gem'} color={project.color ?? 'yellow'} size={28} />
                  <span className="g-truncate">{project.name}</span>
                  <span className="create__cloud-ws">{workspace.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        <p className="create__note">
          <HardDrive size={14} />
          <span>
            Projects are saved in this browser and work offline. No account needed, ever, for the free plan.
            {cloud.status === 'signed-out' && (
              <>
                {' '}
                Have a Tomo Cloud account?{' '}
                <button type="button" className="inline-link" onClick={() => setAuth('signin')}>
                  Sign in
                </button>{' '}
                to open your team’s projects.
              </>
            )}
          </span>
        </p>
      </main>

      <AuthDialog mode={auth} onClose={() => setAuth(null)} onModeChange={setAuth} />
    </div>
  );
}
